/* eslint-disable @typescript-eslint/no-empty-function */
// @file Vuex Store module for Realtime functionality
import * as ActionCable from '@rails/actioncable'
import { getSocket } from 'bits/brahms'
import { captureException } from 'bits/error_tracker'
import window from 'bits/global'
import { __ } from 'bits/intl'
import ping from 'bits/ping'
import debounce from 'lodash.debounce'
import * as Phoenix from 'phoenix'
import PadletApi from 'surface/padlet_api'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

interface WallChannel extends ActionCable.Subscription {
  received: (data: {}) => void
  disconnect: () => void
  present: () => void
  absent: () => void
}

interface DeviceChannel extends ActionCable.Subscription {
  received: (data: {}) => void
}

interface RealtimeState {
  wallChannel: WallChannel | null
  wallChannelLastConnected: number | null
  wallChannelLastDisconnected: number | null
  deviceChannel: DeviceChannel | null
  useBrahms: boolean
  pingContributingStatusToken: string | null
  brahmsToken: string | null
  deviceId: string | null
  brahmsSocket: Phoenix.Socket | null
  actionCableConsumer: ActionCable.Consumer | null
  wallBrahmsChannel: Phoenix.Channel | null
  deviceBrahmsChannel: Phoenix.Channel | null
  dispatchQueue: any[]
  isClearingQueue: boolean
  notificationUid: string | null
  userId: number | null
  isPresencePresent: boolean
}

const defaultState = (): RealtimeState => ({
  wallChannel: null,
  wallChannelLastConnected: null,
  wallChannelLastDisconnected: null,
  deviceChannel: null,
  useBrahms: false,
  pingContributingStatusToken: null,
  brahmsToken: null,
  deviceId: null,
  brahmsSocket: null,
  actionCableConsumer: null,
  wallBrahmsChannel: null,
  deviceBrahmsChannel: null,
  dispatchQueue: [],
  isClearingQueue: false,
  notificationUid: null,
  userId: null,
  isPresencePresent: false,
})

function userNeedsFocus(rootGetters): boolean {
  const isNonChatPostBeingEdited = !!rootGetters['post/postBeingEditedCid']
  // for modal posting we won't suppress updates while the user is writing a post anymore

  const isBusyReacting = !!rootGetters['reaction/postIdBeingReactedTo']
  const isBusyCommenting =
    rootGetters['comment/isCommenting'] &&
    rootGetters['comment/inlineCommentInputLastTypedInMs'] &&
    Date.now() - rootGetters['comment/inlineCommentInputLastTypedInMs'] < 30000
  return (!rootGetters.isUsingNewComposerModal && isNonChatPostBeingEdited) || isBusyReacting || isBusyCommenting
}

const RECONNECT_DELAY = [50, 100, 200, 500, 1000, 2000, 5000]

const RealtimeModule: Module<RealtimeState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    areUpdatesPending(state): boolean {
      return state.dispatchQueue.length > 0
    },
    isConnected(state): boolean {
      const connectedTime = state.wallChannelLastConnected
      const disconnectedTime = state.wallChannelLastDisconnected
      if (!connectedTime) return false
      if (!disconnectedTime) return true
      return connectedTime > disconnectedTime
    },
    isRealtimeDownSinceLastSync(state, getters): (lastSyncTime: number) => boolean {
      return (lastSyncTime: number): boolean => {
        if (!getters.isConnected) return true
        const connectedTime = state.wallChannelLastConnected as number
        return lastSyncTime <= connectedTime
      }
    },
  },
  mutations: {
    INITIALIZE_STATE(state, data): void {
      Object.assign(state, data)
    },
    SET_WALL_CHANNEL(state, wallChannel): void {
      state.wallChannel = wallChannel
    },
    SET_DEVICE_CHANNEL(state, deviceChannel): void {
      state.deviceChannel = deviceChannel
    },
    SET_USE_BRAHMS(state, useBrahms): void {
      state.useBrahms = useBrahms
    },
    SET_BRAHMS_TOKEN(state, brahmsToken): void {
      state.brahmsToken = brahmsToken
    },
    SET_BRAHMS_SOCKET(state, brahmsSocket): void {
      state.brahmsSocket = brahmsSocket
    },
    SET_ACTION_CABLE_CONSUMER(state, actionCableConsumer): void {
      state.actionCableConsumer = actionCableConsumer
    },
    SET_WALL_BRAHMS_CHANNEL(state, wallBrahmsChannel): void {
      state.wallBrahmsChannel = wallBrahmsChannel
    },
    SET_DEVICE_BRAHMS_CHANNEL(state, deviceBrahmsChannel): void {
      state.deviceBrahmsChannel = deviceBrahmsChannel
    },
    QUEUE_UPDATE_ACTION(state, action): void {
      state.dispatchQueue.push(action)
    },
    START_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = true
    },
    CLEAR_DISPATCH_QUEUE(state): void {
      state.dispatchQueue = []
    },
    END_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = false
    },
    SET_NOTIFICATION_UID(state, uid): void {
      state.notificationUid = uid
    },
    CLEAR_NOTIFICATION_UID(state): void {
      state.notificationUid = null
    },
    WALL_CHANNEL_CONNECTED(state): void {
      state.wallChannelLastConnected = Date.now()
    },
    WALL_CHANNEL_DISCONNECTED(state): void {
      state.wallChannelLastDisconnected = Date.now()
    },
    SET_IS_PRESENCE_PRESENT(state, isPresencePresent): void {
      state.isPresencePresent = isPresencePresent
    },
  },
  actions: {
    initializeState({ commit }, data): void {
      commit('INITIALIZE_STATE', data)
    },

    connect({ commit, dispatch, state }, { actionCableConsumer }: { actionCableConsumer: ActionCable.Consumer }): void {
      if (actionCableConsumer) {
        commit('SET_ACTION_CABLE_CONSUMER', actionCableConsumer)
      }
      if (state.useBrahms) {
        dispatch('connectToBrahms')
      } else {
        dispatch('connectToActionCable')
      }

      dispatch('subscribeToStoreChanges')
    },

    connectToBrahms({ dispatch, commit, state }): void {
      if (!state.brahmsToken) return

      const brahmsConnection = getSocket({ accessToken: state.brahmsToken, deviceId: state.deviceId }, (tries) => {
        return RECONNECT_DELAY[tries - 1] || 10000
      })

      if (brahmsConnection) {
        commit('SET_BRAHMS_SOCKET', brahmsConnection)

        brahmsConnection.connect()

        dispatch('subscribeWallBrahmsChannel')
        dispatch('subscribeDeviceBrahmsChannel')
        dispatch('subscribeUserBrahmsChannel')
      }
    },

    connectToActionCable({ dispatch, state }): void {
      dispatch('subscribeWallChannel', state.actionCableConsumer)
      dispatch('subscribeDeviceChannel', state.actionCableConsumer)
    },

    refreshTokenAndConnect({ commit, dispatch, state }): void {
      PadletApi.BrahmsToken.get({ token: state.pingContributingStatusToken }).then((data) => {
        commit('SET_BRAHMS_TOKEN', data.brahmsToken)
        dispatch('connectToBrahms')
      })
    },

    disconnectBrahms({ commit, state }): void {
      state.brahmsSocket && state.brahmsSocket.disconnect()
      commit('SET_USE_BRAHMS', false)
      commit('SET_BRAHMS_SOCKET', null)
    },

    disconnect({ state }): void {
      state.wallChannel && state.wallChannel.unsubscribe()
      state.deviceChannel && state.deviceChannel.unsubscribe()
    },

    absent({ commit, state }): void {
      if (state.isPresencePresent) {
        commit('SET_IS_PRESENCE_PRESENT', false)
        if (state.useBrahms) {
          state.wallBrahmsChannel && state.wallBrahmsChannel.push('absent', {})
          return
        }
        state.wallChannel && state.wallChannel.absent()
      }
    },

    present({ commit, state }): void {
      commit('SET_IS_PRESENCE_PRESENT', true)
      if (state.useBrahms) {
        state.wallBrahmsChannel && state.wallBrahmsChannel.push('present', {})
        return
      }
      state.wallChannel && state.wallChannel.present()
    },

    subscribeWallChannel({ dispatch, commit, rootGetters, state }, actionCableConsumer: ActionCable.Consumer): void {
      const wallId = rootGetters.wallId
      const deviceId = state.deviceId
      const wallChannel = actionCableConsumer.subscriptions.create(
        { channel: 'WallChannel', wall_id: wallId },
        {
          received(data): void {
            dispatch('handleMessage', data)
          },
          disconnect(): void {
            this.absent()
          },
          connected(): void {
            commit('WALL_CHANNEL_CONNECTED')
          },
          disconnected(): void {
            commit('WALL_CHANNEL_DISCONNECTED')
          },
          present(): void {
            this.perform('present', { wall_id: wallId, device_id: deviceId })
          },
          absent(): void {
            this.perform('absent', { wall_id: wallId, device_id: deviceId })
          },
        },
      )
      commit('SET_WALL_CHANNEL', wallChannel)
    },

    subscribeWallBrahmsChannel({ commit, rootGetters, state, dispatch }): void {
      const wallId = rootGetters.wallId
      if (state.useBrahms && state.brahmsSocket) {
        try {
          const wallBrahmsChannel: Phoenix.Channel = state.brahmsSocket.channel(`wall:${wallId}`)

          wallBrahmsChannel.on('new_msg', (data) => {
            dispatch('handleMessage', data)
          })
          wallBrahmsChannel.onClose(() => {
            commit('WALL_CHANNEL_DISCONNECTED')
          })
          wallBrahmsChannel.join().receive('ok', () => commit('WALL_CHANNEL_CONNECTED'))

          commit('SET_WALL_BRAHMS_CHANNEL', wallBrahmsChannel)
        } catch (e) {
          captureException(e)
        }
      }
    },

    subscribeDeviceChannel({ dispatch, commit, state }, actionCableConsumer: ActionCable.Consumer): void {
      const deviceChannel = actionCableConsumer.subscriptions.create(
        { channel: 'DeviceChannel', device_id: state.deviceId },
        {
          received(data): void {
            dispatch('handleMessage', data)
          },
        },
      )
      commit('SET_DEVICE_CHANNEL', deviceChannel)
    },

    subscribeDeviceBrahmsChannel({ dispatch, commit, state }): void {
      if (state.useBrahms && state.brahmsSocket) {
        try {
          const deviceBrahmsChannel: Phoenix.Channel = state.brahmsSocket.channel(`device:${state.deviceId}`)

          deviceBrahmsChannel.on('new_msg', (msg) => {
            dispatch('handleMessage', msg)
          })
          deviceBrahmsChannel.join()

          commit('SET_DEVICE_BRAHMS_CHANNEL', deviceBrahmsChannel)
        } catch (e) {
          captureException(e)
        }
      }
    },

    subscribeUserBrahmsChannel({ state }): void {
      if (state.useBrahms && state.brahmsSocket) {
        const userId = state.userId
        const userChannel: Phoenix.Channel = state.brahmsSocket.channel(`user:${userId}`)
        userChannel.join()
      }
    },

    subscribeToStoreChanges({ dispatch, getters, rootGetters }): void {
      const flushUpdates = (): void => {
        if (getters.areUpdatesPending && !userNeedsFocus(rootGetters)) {
          dispatch('dispatchAllQueuedActions')
        }
      }
      const debouncedFlushUpdates = debounce(flushUpdates, 200, { leading: true })
      this.subscribe((): void => debouncedFlushUpdates())
    },

    pingContributingStatus({ state }): void {
      state.wallBrahmsChannel?.push('ping_contributing_status', {})
    },

    updateContributingStatus({ state }, { status }): void {
      state.wallBrahmsChannel?.push('update_contributing_status', { status })
    },

    handleMessage({ dispatch, rootState }, messageObj): void {
      if (!messageObj || !messageObj.event || messageObj.uid === rootState.uid) return
      const payload = JSON.parse(messageObj.message)
      // Signal that realtime is working
      ping('realtime', { rate: 20000 })
      const dispatchNow = (type: string): Promise<any> => dispatch(type, payload, { root: true })
      const queue = (type: string): Promise<any> => dispatch('queueUpdateAction', { type, payload })
      // We add a delay if the post has an attachment, so that we have sufficient
      // lead time for request coalescing to come into play.
      // See original commit: https://github.com/padlet/mozart/commit/9f9e0d60f3934c0e1c6ef411af97e1e24f429151
      const delay = (type: string): void => {
        const upTo1andHalfSecDelay = (Math.random() + 0.5) * 1000
        payload.attachment
          ? setTimeout((): void => {
              queue(type)
            }, upTo1andHalfSecDelay)
          : queue(type)
      }

      // Right now we are calling 'remote' actions in the modules themselves.
      // Eventually, the modules will have no concept of remote updates.
      // They'll only have simple CRUD operations that'll be called by this guy.
      const handlers = {
        edit_wall(): void {
          dispatchNow('updateWall')
        },
        delete_wall(): void {
          window.location.href = '/'
        },
        add_section(): void {
          dispatchNow('section/newSectionRemote')
        },
        edit_section(): void {
          dispatchNow('section/editSectionRemote')
        },
        delete_section(): void {
          dispatchNow('section/deleteSectionRemote')
        },
        add_wish(): void {
          delay('post/newPostRemote')
        },
        edit_wish(): void {
          delay('post/editPostRemote')
        },
        edit_wish_order(): void {
          delay('post/editPostOrderRemote')
        },
        delete_wish(): void {
          dispatchNow('deletePostRemote')
        },
        add_comment(): void {
          queue('comment/newCommentRemote')
        },
        edit_comment(): void {
          queue('comment/editCommentRemote')
        },
        delete_comment(): void {
          queue('comment/deleteCommentRemote')
        },
        add_reaction(): void {
          dispatchNow('reaction/newReactionRemote')
        },
        edit_reaction(): void {
          dispatchNow('reaction/editReactionRemote')
        },
        delete_reaction(): void {
          dispatchNow('reaction/deleteReactionRemote')
        },
        add_connection(): void {
          dispatchNow('postConnection/newConnectionRemote')
        },
        delete_connection(): void {
          dispatchNow('postConnection/deleteConnectionRemote')
        },
        edit_table_layout(): void {
          queue('table/editLayoutRemote')
        },
        edit_table_row(): void {
          queue('table/editRowRemote')
        },
        edit_table_col(): void {
          queue('table/editColRemote')
        },
        contributing_status(): void {
          dispatchNow('contributingStatus/updateRemote')
        },
        ping_contributing_status(): void {
          dispatchNow('contributingStatus/pushSetIfActive')
        },
        login(): void {
          window.location.reload()
        },
        logout(): void {
          window.location.reload()
        },
        collaborator_left(): void {
          dispatchNow('wallAccessSettings/removeCollaboratorRemotely')
        },
      }

      const handler = handlers[messageObj.event]
      handler && handler()
    },

    queueUpdateAction({ dispatch, commit, getters, rootGetters }, { type, payload }): void {
      if (userNeedsFocus(rootGetters)) {
        if (!getters.areUpdatesPending) dispatch('setUpdatesPendingNotification')
        commit('QUEUE_UPDATE_ACTION', { type, payload })
      } else {
        dispatch(type, payload, { root: true })
      }
    },

    setUpdatesPendingNotification({ dispatch, commit }): void {
      dispatch(
        'snackbars/setSnackbar',
        {
          message: __('Padlet updated elsewhere.'),
          actionText: __('Update'),
          actionName: 'realtime/dispatchAllQueuedActions',
          persist: true,
        },
        { root: true },
      ).then((uid): void => commit('SET_NOTIFICATION_UID', uid))
    },

    clearUpdatesPendingNotification({ dispatch, commit, state }): void {
      dispatch('snackbars/clearSnackbar', state.notificationUid, { root: true })
      commit('CLEAR_NOTIFICATION_UID')
    },

    dispatchAllQueuedActions({ commit, dispatch, state }): void {
      if (state.isClearingQueue) return
      commit('START_CLEARING_DISPATCH_QUEUE')
      state.dispatchQueue.forEach(({ type, payload }): Promise<any> => dispatch(type, payload, { root: true }))
      dispatch('clearDispatchQueue')
      commit('END_CLEARING_DISPATCH_QUEUE')
    },

    clearDispatchQueue({ dispatch, commit }): void {
      dispatch('clearUpdatesPendingNotification')
      commit('CLEAR_DISPATCH_QUEUE')
    },
  },
}

export default RealtimeModule
export { RealtimeState }
